import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { adminApi } from '@/api/admin.api';
import { extractErrorMessage } from '@/lib/errors';

export function useTenantsList() {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.listTenants(),
    select: (res) => res.data.data,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.createTenant,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      toast.success('Cliente criado com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useUpdateTenantStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' | 'trial' }) =>
      adminApi.updateTenantStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      toast.success('Status atualizado');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.deleteTenant,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      toast.success('Cliente removido');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useTenantModules(tenantId: string) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'modules'],
    queryFn: () => adminApi.listTenantModules(tenantId),
    select: (res) => res.data.data,
  });
}

export function useSetTenantModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, module, enabled }: { id: string; module: string; enabled: boolean }) =>
      adminApi.setTenantModule(id, module, enabled),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants', id, 'modules'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useTenantUsers(tenantId: string | null) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'users'],
    queryFn: () => adminApi.listTenantUsers(tenantId!),
    select: (res) => res.data.data,
    enabled: !!tenantId,
  });
}

export function useResetTenantUserPassword() {
  return useMutation({
    mutationFn: ({
      tenantId,
      userId,
      password,
    }: {
      tenantId: string;
      userId: string;
      password: string;
    }) => adminApi.resetTenantUserPassword(tenantId, userId, password),
    onSuccess: () => toast.success('Senha redefinida com sucesso'),
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useAdminUplogStats() {
  return useQuery({
    queryKey: ['admin', 'uplog-stats'],
    queryFn: () => adminApi.getAdminUplogStats(),
    select: (res) => res.data.data,
    refetchInterval: 60_000,
  });
}
